import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../api/client";

/**
 * Médiathèque.
 *
 * Deux exigences gouvernent cet écran :
 *
 * 1. **Les échecs sont rendus fichier par fichier.** On téléverse depuis un
 *    chantier, avec une connexion médiocre, dix photos d'un coup. Si trois
 *    échouent, l'utilisateur doit savoir lesquelles — un « le téléversement a
 *    échoué » global lui ferait tout recommencer.
 *
 * 2. **Le texte alternatif est signalé tant qu'il manque.** Les contrôles de
 *    qualité refusent une réalisation dont une image n'a pas d'`alt`, et c'est
 *    d'abord une question d'accessibilité. Le dire ici évite de découvrir le
 *    refus au moment de publier.
 */

type Media = {
  id: number;
  chantierId: number | null;
  chemin: string;
  cheminMiniature: string;
  /** Servies par l'API derrière la session : jamais d'accès direct au disque. */
  url: string;
  urlMiniature: string;
  type: string;
  alt: string;
  legende: string;
  largeur: number;
  hauteur: number;
  poids: number;
  ordre: number;
  createdAt: string;
};

/**
 * Nom lisible d'un média. Le fichier stocké porte un nom aléatoire — le nom
 * d'origine n'est jamais conservé, pour éviter la traversée de chemin et la
 * fuite d'information par le nom lui-même. On affiche donc la date et les
 * dimensions, seules informations dont dispose l'utilisateur pour se repérer.
 */
function libelleMedia(media: Media): string {
  return `Photo du ${media.createdAt.slice(0, 10)} · ${media.largeur}×${media.hauteur}`;
}

type Echec = { nom: string; erreur: string };

const LIBELLES_TYPE: Record<string, string> = {
  avant: "Avant",
  pendant: "Pendant",
  apres: "Après",
  libre: "Libre",
};

function poidsLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

export function Medias() {
  const client = useQueryClient();
  const champFichiers = useRef<HTMLInputElement | null>(null);
  const [echecs, setEchecs] = useState<Echec[]>([]);

  const medias = useQuery({
    queryKey: ["medias"],
    queryFn: () => api.get<{ medias: Media[] }>("/api/medias"),
  });

  const televerser = useMutation({
    mutationFn: async (fichiers: FileList) => {
      const formulaire = new FormData();
      for (const fichier of fichiers) formulaire.append("fichiers", fichier);

      const reponse = await fetch("/api/medias", {
        method: "POST",
        body: formulaire,
        credentials: "include",
      });
      // 400 avec zéro réussite reste une réponse exploitable : elle porte le
      // détail des échecs, qu'on veut afficher plutôt qu'une erreur générique.
      return (await reponse.json()) as { reussites?: Media[]; echecs?: Echec[]; erreur?: string };
    },
    onSuccess: (resultat) => {
      setEchecs(
        resultat.echecs ?? (resultat.erreur ? [{ nom: "—", erreur: resultat.erreur }] : []),
      );
      client.invalidateQueries({ queryKey: ["medias"] });
    },
    onError: (erreur) => {
      setEchecs([{ nom: "—", erreur: erreur.message }]);
    },
  });

  const modifierAlt = useMutation({
    mutationFn: ({ id, alt }: { id: number; alt: string }) =>
      api.patch<{ media: Media }>(`/api/medias/${id}`, { alt }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["medias"] }),
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => api.supprimer<unknown>(`/api/medias/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ["medias"] }),
  });

  const liste = medias.data?.medias ?? [];
  const sansAlt = liste.filter((media) => media.alt.trim() === "");

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        Médiathèque
      </Typography>

      <Paper sx={{ p: 3 }}>
        <input
          ref={champFichiers}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          multiple
          hidden
          onChange={(evenement) => {
            const fichiers = evenement.target.files;
            if (fichiers && fichiers.length > 0) televerser.mutate(fichiers);
            // Réinitialisé pour que reprendre le même fichier redéclenche l'envoi.
            evenement.target.value = "";
          }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "center" }}>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={televerser.isPending}
            onClick={() => champFichiers.current?.click()}
          >
            {televerser.isPending ? "Envoi en cours…" : "Ajouter des photos"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            JPEG, PNG, WebP ou AVIF. 10 Mo par fichier au maximum. Les données de géolocalisation
            sont retirées automatiquement.
          </Typography>
        </Stack>

        {televerser.isPending && <LinearProgress sx={{ mt: 2 }} />}

        {echecs.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setEchecs([])}>
            <AlertTitle>
              {echecs.length === 1
                ? "Un fichier n'a pas pu être ajouté"
                : `${echecs.length} fichiers n'ont pas pu être ajoutés`}
            </AlertTitle>
            {/* Nommer chaque fichier : sans cela, il faudrait tout recommencer. */}
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {echecs.map((echec) => (
                <Typography key={`${echec.nom}-${echec.erreur}`} component="li" variant="body2">
                  <strong>{echec.nom}</strong> — {echec.erreur}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}
      </Paper>

      {sansAlt.length > 0 && (
        <Alert severity="info" icon={<WarningAmberIcon />}>
          {sansAlt.length === 1
            ? "Une photo n'a pas de description."
            : `${sansAlt.length} photos n'ont pas de description.`}{" "}
          Une réalisation dont une image n'est pas décrite sera refusée à la publication, et l'image
          restera invisible pour les personnes qui utilisent un lecteur d'écran.
        </Alert>
      )}

      {medias.isPending ? (
        <LinearProgress />
      ) : liste.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Aucune photo pour l'instant. Les photos de chantier alimentent les pages de
            réalisations, qui portent le référencement local.
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
          }}
        >
          {liste.map((media) => (
            <Card key={media.id}>
              <CardMedia
                component="img"
                src={media.urlMiniature}
                alt={media.alt || libelleMedia(media)}
                loading="lazy"
                sx={{ aspectRatio: "4 / 3", objectFit: "cover", bgcolor: "grey.100" }}
              />
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
                >
                  <Chip size="small" label={LIBELLES_TYPE[media.type] ?? media.type} />
                  <Typography variant="caption" color="text.secondary">
                    {media.largeur}×{media.hauteur} · {poidsLisible(media.poids)}
                  </Typography>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small"
                      aria-label={`Supprimer : ${libelleMedia(media)}`}
                      onClick={() => supprimer.mutate(media.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <TextField
                  fullWidth
                  size="small"
                  label="Description de l'image"
                  defaultValue={media.alt}
                  error={media.alt.trim() === ""}
                  helperText={
                    media.alt.trim() === ""
                      ? "Requise avant publication"
                      : "Décrit l'image pour les lecteurs d'écran et les moteurs"
                  }
                  // Enregistré à la sortie du champ plutôt qu'à chaque frappe :
                  // une requête par caractère saturerait l'API pour rien.
                  onBlur={(evenement) => {
                    const alt = evenement.target.value.trim();
                    if (alt !== media.alt) modifierAlt.mutate({ id: media.id, alt });
                  }}
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Stack>
  );
}
